import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { RouteProps, useHistory } from 'react-router-dom';
import Title from '../../components/Title';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import { Order, OrderListResponse, RemovalOrdersResponse, RemovalQuoteRequestsResponse } from '../../models/APIModels';
import Moment from 'moment';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import SubmitButton from '../../components/SubmitButton';
import FileDownload from 'js-file-download';

function AdminDashboardComponent({
  history,
}: RouteComponentProps) {
  const [orders, setOrders] = useState<any[]>([]);
  const { user: loggedInUser } = useContext(UserContext);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fetch regular orders, removal orders, AND quote requests
    Promise.all([
      axiosKK.get<OrderListResponse>('/orders'),
      axiosKK.get<RemovalOrdersResponse>('/admin/removals/orders').catch(() => ({ data: { orders: [] } })),
      axiosKK.get<RemovalQuoteRequestsResponse>('/admin/removals/quotes').catch(() => ({ data: { quotes: [] } }))
    ]).then(([regularOrdersResult, removalOrdersResult, quoteRequestsResult]) => {
      // Mark quote requests with itemType for identification
      const quotesWithType = quoteRequestsResult.data.quotes.map(q => ({ 
        ...q, 
        itemType: 'quote' as const 
      }));
      
      // Merge all types
      const allOrders = [
        ...regularOrdersResult.data.orders,
        ...removalOrdersResult.data.orders,
        ...quotesWithType
      ];
      
      // Sort by creation date (most recent first)
      setOrders(allOrders.sort((a, b) => b.createdAt - a.createdAt));
    }).catch((error) => {
      console.error('Error fetching orders:', error);
      setOrders([]);
    });
  }, []);

  return (
    <>
      {(function () {
        switch (loggedInUser?.role) {
          case Role.Admin:
            return <Title>Admin Dashboard</Title>;
          case Role.Warehouse:
            return <Title>Warehouse Dashboard</Title>;
          default:
            return <Title>Dashboard</Title>;
        }
      })()}

      {loggedInUser && (
        <p className="grey">Welcome back, {loggedInUser?.firstName}</p>
      )}

      <section className="option-box-container">
        {loggedInUser?.role === Role.Admin && (
          <>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/products');
              }}
            >
              <div className="img">
                <i className="fas fa-sign-in-alt" />
              </div>
              <div className="content">
                <h5>Products</h5>
                <p>Create and Edit Products</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/discounts');
              }}
            >
              <div className="img">
                <i className="fas fa-sign-in-alt" />
              </div>
              <div className="content">
                <h5>Discounts</h5>
                <p>Create and Edit Discounts</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/users');
              }}
            >
              <div className="img">
                <i className="fas fa-map-marker-alt" />
              </div>
              <div className="content">
                <h5>Manage Admins</h5>
                <p>Create and Edit Admin Profiles</p>
              </div>
            </div>
          </>
        )}
        <div
          className="each-box"
          onClick={() => {
            history.push('/admin/customers');
          }}
        >
          <div className="img">
            <i className="fas fa-map-marker-alt" />
          </div>
          <div className="content">
            <h5>Customer Profiles</h5>
            <p>Create and Edit Customer Profiles</p>
          </div>
        </div>
      </section>

      <div className="line" />

      <Title>Orders</Title>
      <section className="search-bar">
        <SubmitButton
          onClick={() => {
            axiosKK
              .get(`/orders/CSV`, {
                responseType: 'blob',
              })
              .then((response) => {
                FileDownload(response.data, `OrderBoxes.csv`);
              });
          }}
        >
          Download CSV
        </SubmitButton>
      </section>
      <section className="admin-all-orders-info">
        <div className="table">
          <div className="thead">
            <div className="tr">
              <div className="td id">ID</div>
              <div className="td date">Created Date</div>
              <div className="td address">Customer Name</div>
              <div className="td yes-and-no">Fulfilled</div>
              <div className="td yes-and-no">Order Type</div>
              <div className="td date">Collection Date</div>
              <div className="td date">Delivery Date</div>
              <div className="td yes-and-no">Assignment</div>
            </div>
          </div>
          <div className="tbody">
            {orders.map((each) => {
              // Create unique key by prefixing with type
              const itemType = (each as any).itemType || each.orderType || 'order';
              const uniqueKey = `${itemType}-${each.id}`;
              return <Row key={uniqueKey} order={each} />;
            })}
          </div>
        </div>
      </section>
    </>
  );
}

interface RowProps extends RouteProps {
  order: Order;
}

function Row({ order }: RowProps) {
  const history = useHistory();
  const isRemovalOrder = order.orderType === 'removal';
  const isQuoteRequest = (order as any).itemType === 'quote';

  return (
    <div
      className="tr"
      onClick={() => {
        if (isQuoteRequest) {
          history.push(`/admin/removals/quote/${order.id}`, { quote: order });
        } else {
          history.push(`/admin/order/${order.id}`, { order });
        }
      }}
    >
      <Label>ID</Label>
      <div className="td id">{order.id}</div>
      
      <Label>Created At</Label>
      <div className="td date">
        {Moment(order.createdAt).format('dddd, MMM Do YYYY')}
      </div>
      
      <Label>Customer Name</Label>
      <div className="td address">
        {isQuoteRequest 
          ? (order as any).customer 
            ? `${(order as any).customer.firstName} ${(order as any).customer.lastName}` 
            : (order as any).customerEmail
          : `${order.customer.firstName} ${order.customer.lastName}`}
      </div>
      
      <Label>Fulfilled</Label>
      <div className="td yes-and-no">
        {isQuoteRequest ? 'N/A' : isRemovalOrder ? 'N/A' : (order.roomPackIncluded ? 'Yes' : 'No')}
      </div>
      
      <Label>Order Type</Label>
      <div className="td yes-and-no">
        {isQuoteRequest
          ? `Quote - ${(order as any).tierName}`
          : isRemovalOrder
          ? 'Removal'
          : order.suitcaseOrder
            ? 'Suitcase'
            : order.storageRequired
              ? 'Storage'
              : 'Shipping'}
      </div>
      
      <Label>Collection Date</Label>
      <div className="td date">
        {isQuoteRequest
          ? 'Pending Quote'
          : isRemovalOrder 
          ? (order.moveDate ? Moment(order.moveDate).format('dddd, MMM Do YYYY') : 'Not Set')
          : (order.collectionDate ? Moment(order.collectionDate).format('dddd, MMM Do YYYY') : 'Not Set')}
      </div>
      
      <Label>Delivery Date</Label>
      <div className="td date">
        {isQuoteRequest
          ? (order as any).status.toUpperCase()
          : order.deliveryDate
          ? Moment(order.deliveryDate).format('dddd, MMM Do YYYY')
          : 'Not Set'}
      </div>

      <Label>Assignment</Label>
      <div className="td yes-and-no">
        {isQuoteRequest || isRemovalOrder ? (
          ''
        ) : order.storageRequired ? (
          order.assignedDriverId && order.assignedWarehouseId ? (
            <span className="assignment-badge assignment-full">Full</span>
          ) : order.assignedDriverId ? (
            <span className="assignment-badge assignment-partial">Courier</span>
          ) : order.assignedWarehouseId ? (
            <span className="assignment-badge assignment-partial">Warehouse</span>
          ) : (
            <span className="assignment-badge assignment-none">None</span>
          )
        ) : (
          ''
        )}
      </div>
    </div>
  );
}

export default withRouter(AdminDashboardComponent as any);
